import { reportProjectDataLoadingFailure, ServiceWorkerFailureKind } from 'services/serviceWorker/diagnostics';
import { logError } from 'setup/logger/logError';
import { isChannelRequestTimeout } from 'shared/channels';
import { projectDataClient } from 'shared/domain/projectData/projectDataClient';
import { isNoProjectAccessDownloadFailure } from 'shared/domain/projectData/projectDataDownloadError';
import {
  isProjectDataDownloaded,
  isProjectDataDownloadIdle,
  ProjectDataKey,
} from 'shared/domain/projectData/projectDataModel';
import type { ZustandSet } from 'store/types';
import {
  clearProjectDataLoadingFailure,
  createInitialProjectDataLoading,
  getPendingProjectData,
  getProjectDataLoadingFailureLogLevel,
  markAllProjectDataPending,
  markProjectDataLoadingNeedsWorkerUpdate,
  ProjectDataLoading,
  ProjectDataLoadingFailureReason,
  replacesProjectDataLoadingFailure,
  setProjectDataLoadingFailure,
  setProjectDataPending,
} from './loadingModel';
import type { ProjectStore } from './types';

/**
 * How often the page asks how the download goes. Each ask keeps the worker
 * awake and starts again the downloads that stopped; a download that still
 * did not start at the next ask has stalled.
 */
export const PROJECT_DATA_PROGRESS_CHECK_INTERVAL_MS = 30_000;

export type ProjectLoadingActions = {
  /** The worker downloads the project data again (project switch, data flush). */
  markAllDataPending: () => void;
  /** A standard user has no inspections, so the loading does not wait for them. */
  setWaitsForInspectionData: (waits: boolean) => void;
  resetDataLoading: () => void;
  /** Waits for the worker to download the pending data, until it is stored or the loading fails. */
  startProjectDataLoading: () => void;
  stopProjectDataLoading: () => void;
  /** After a failure: waits again for the data still missing. */
  retryProjectDataLoading: () => void;
  /** The worker dropped the data: all of it is pending again and, while loading runs, waited for again. */
  reloadProjectDataAfterFlush: () => void;
};

type LoadingSession = {
  readonly generation: number;
  progressCheck: ReturnType<typeof setInterval> | undefined;
  /** Data the worker reported idle at the previous check. */
  idleKeys: ReadonlySet<ProjectDataKey>;
};

function toFailureReason(error: unknown): ProjectDataLoadingFailureReason {
  if (isNoProjectAccessDownloadFailure(error)) {
    return ProjectDataLoadingFailureReason.noProjectAccess;
  }
  return isChannelRequestTimeout(error)
    ? ProjectDataLoadingFailureReason.workerUnresponsive
    : ProjectDataLoadingFailureReason.downloadFailed;
}

/** Whether the selected project's data is downloaded by the worker, kept in `store/project`. */
export function createProjectLoadingActions(
  set: ZustandSet<ProjectStore>,
  getLoading: () => ProjectDataLoading
): ProjectLoadingActions {
  // Answers of a loading that was stopped or started again (project switch, flush) are ignored.
  let generation = 0;
  let session: LoadingSession | null = null;

  const updateLoading = (update: (loading: ProjectDataLoading) => ProjectDataLoading): void =>
    set((state) => {
      const loading = update(state.loading);
      return loading === state.loading ? state : { loading };
    });

  const isCurrent = (loadingGeneration: number): boolean => session?.generation === loadingGeneration;

  const stopProgressChecks = (loadingSession: LoadingSession): void => {
    clearInterval(loadingSession.progressCheck);
    loadingSession.progressCheck = undefined;
  };

  const fail = (
    loadingSession: LoadingSession,
    reason: ProjectDataLoadingFailureReason,
    keys: readonly ProjectDataKey[],
    error?: unknown
  ): void => {
    if (!isCurrent(loadingSession.generation) || !replacesProjectDataLoadingFailure(getLoading(), reason)) {
      return;
    }
    stopProgressChecks(loadingSession);
    updateLoading((loading) => setProjectDataLoadingFailure(loading, { reason, keys, needsWorkerUpdate: false }));
    reportProjectDataLoadingFailure(reason, { keys, error }, getProjectDataLoadingFailureLogLevel(reason)).then(
      (failureKind) => {
        if (isCurrent(loadingSession.generation) && failureKind === ServiceWorkerFailureKind.outdatedWorker) {
          updateLoading(markProjectDataLoadingNeedsWorkerUpdate);
        }
      },
      (reportError: unknown) =>
        logError('Project data loading failure could not be reported', { reason, keys, error, reportError })
    );
  };

  const waitForDownload = (loadingSession: LoadingSession, key: ProjectDataKey): void => {
    projectDataClient.ensureDownloaded(key).then(
      () => {
        if (isCurrent(loadingSession.generation)) {
          updateLoading((loading) => setProjectDataPending(loading, key, false));
        }
      },
      (error: unknown) => fail(loadingSession, toFailureReason(error), [key], error)
    );
  };

  const checkProgress = async (loadingSession: LoadingSession): Promise<void> => {
    const pending = getPendingProjectData(getLoading());
    if (pending.length === 0) {
      stopProgressChecks(loadingSession);
      return;
    }
    let progress;
    try {
      progress = await projectDataClient.continueDownload(pending);
    } catch (error) {
      fail(loadingSession, ProjectDataLoadingFailureReason.workerUnresponsive, pending, error);
      return;
    }
    if (!isCurrent(loadingSession.generation)) {
      return;
    }
    pending
      .filter((key) => isProjectDataDownloaded(progress[key]))
      .forEach((key) => updateLoading((loading) => setProjectDataPending(loading, key, false)));

    const idleKeys = pending.filter((key) => isProjectDataDownloadIdle(progress[key]));
    const stalledKeys = idleKeys.filter((key) => loadingSession.idleKeys.has(key));
    loadingSession.idleKeys = new Set(idleKeys);
    if (stalledKeys.length > 0) {
      fail(loadingSession, ProjectDataLoadingFailureReason.stalled, stalledKeys);
    }
  };

  const stopProjectDataLoading = (): void => {
    if (session) {
      stopProgressChecks(session);
      session = null;
    }
  };

  const startProjectDataLoading = (): void => {
    stopProjectDataLoading();
    generation += 1;
    const loadingSession: LoadingSession = { generation, progressCheck: undefined, idleKeys: new Set() };
    session = loadingSession;
    updateLoading(clearProjectDataLoadingFailure);

    const pending = getPendingProjectData(getLoading());
    if (pending.length === 0) {
      return;
    }
    loadingSession.progressCheck = setInterval(
      () => void checkProgress(loadingSession),
      PROJECT_DATA_PROGRESS_CHECK_INTERVAL_MS
    );
    pending.forEach((key) => waitForDownload(loadingSession, key));
  };

  return {
    markAllDataPending: () => updateLoading(markAllProjectDataPending),

    setWaitsForInspectionData: (waits) =>
      updateLoading((loading) =>
        loading.waitsForInspectionData === waits ? loading : { ...loading, waitsForInspectionData: waits }
      ),

    resetDataLoading: () => set({ loading: createInitialProjectDataLoading() }),

    startProjectDataLoading,
    stopProjectDataLoading,
    retryProjectDataLoading: startProjectDataLoading,

    reloadProjectDataAfterFlush: () => {
      updateLoading(markAllProjectDataPending);
      if (session) {
        startProjectDataLoading();
      }
    },
  };
}
